export const getCommissionRate = (marketType, transactionType, settings) => {
  if (transactionType === 'bonus') return 0;
  
  // کارمزد فروش فزاینده معادل کارمزد فروش عادی در نظر گرفته می‌شود
  const mappedType = transactionType === 'short_sell' ? 'sell' : transactionType;
  
  const settingsMap = {};
  settings.forEach(s => { settingsMap[s.key] = s.value; });
  const key = `${marketType}_${mappedType}_comm`.toLowerCase();
  return settingsMap[key] || 0;
};

/**
 * محاسبه سود و زیان و هزینه فرصت برای پوزیشن‌های فروش فزاینده و خرید عادی آپشن
 * @param {Object} step - اطلاعات پله معاملاتی
 * @param {number} currentPrice - قیمت فعلی پریمیوم در بازار
 * @param {number} bankRate - نرخ سود بانکی
 */
export const calculateOptionStepPerformance = (step, currentPrice, bankRate) => {
  const isShortSell = step.Type === 'short_sell';
  const quantity = step.Quantity;
  const entryPrice = step.Price;
  const blockedAmount = step.BlockedAmount || 0; // وجه تضمین اولیه برای فروش فزاینده
  
  // محاسبه روزهای سپری شده از معامله
  const entryDate = new Date(step.Date);
  const today = new Date();
  const diffTime = Math.abs(today - entryDate);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) || 1;

  // ۱. محاسبه کارمزدها
  const entryCommission = entryPrice * quantity * 0.001; // پیش‌فرض یا بر اساس تنظیمات
  const exitCommission = currentPrice * quantity * 0.001;
  const totalCommission = entryCommission + exitCommission;

  // ۲. محاسبه هزینه فرصت (یا سود تضمین وجه بلوکه شده)
  let opportunityCost = 0;
  if (isShortSell) {
    // در فروش فزاینده، به وجه تضمین اولیه سود تعلق می‌گیرد و به سود کل اضافه می‌شود
    opportunityCost = (blockedAmount * bankRate * diffDays) / 365;
  } else {
    // در خرید عادی، هزینه فرصت سرمایه درگیر از سود کسر می‌شود
    const capitalInvolved = entryPrice * quantity;
    opportunityCost = -((capitalInvolved * bankRate * diffDays) / 365);
  }

  // ۳. محاسبه سود و زیان خالص بازار (سود فزاینده با کاهش قیمت است)
  let priceDiff = 0;
  if (isShortSell) {
    priceDiff = entryPrice - currentPrice; // کاهش پریمیوم = سود
  } else {
    priceDiff = currentPrice - entryPrice; // افزایش پریمیوم = سود
  }
  
  const marketProfitLoss = priceDiff * quantity;
  const finalNetProfitLoss = marketProfitLoss - totalCommission + opportunityCost;

  return {
    marketProfitLoss,
    opportunityCost,
    totalCommission,
    finalNetProfitLoss,
    isShortSell
  };
};


export const getBankInterestRate = (settings) => {
  const settingsMap = {};
  settings.forEach(s => { settingsMap[s.key] = s.value; });
  return settingsMap['bank_interest_rate'] || 0.20;
};

export const normalizePersianText = (text) => {
  if (!text || typeof text !== 'string') return String(text || '');
  const mapping = {
    'ي': 'ی', 'ك': 'ک',
    '٠': '0', '١': '1', '٢': '2', '٣': '3', '٤': '4',
    '٥': '5', '٦': '6', '٧': '7', '٨': '8', '٩': '9'
  };
  let result = text;
  for (const [k, v] of Object.entries(mapping)) {
    result = result.replace(new RegExp(k, 'g'), v);
  }
  return result.trim();
};

/**
 * تبدیل تاریخ جلالی به میلادی
 */
export const jalaliToGregorian = (jy, jm, jd) => {
  jy = parseInt(jy);
  jm = parseInt(jm);
  jd = parseInt(jd);

  let gy = jy <= 979 ? 621 : 1600;
  jy -= jy <= 979 ? 0 : 979;

  let days = (jy <= 979 ? 0 : 365 * 33) +
    365 * jy +
    Math.floor(jy / 33) * 8 +
    Math.floor(((jy % 33) + 3) / 4) +
    78 +
    jd +
    (jm < 7 ? (jm - 1) * 31 : (jm - 7) * 30 + 186);

  gy += 400 * Math.floor(days / 146097);
  days %= 146097;

  if (days > 36524) {
    gy += 100 * Math.floor(--days / 36524);
    days %= 36524;
    if (days >= 365) days++;
  }

  gy += 4 * Math.floor(days / 1461);
  days %= 1461;

  if (days > 365) {
    gy += Math.floor((days - 1) / 365);
    days = (days - 1) % 365;
  }

  let gd = days + 1;
  const gMonthDays = [0, 31, gy % 4 === 0 && (gy % 100 !== 0 || gy % 400 === 0) ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

  let gm = 0;
  for (let i = 1; i <= 12; i++) {
    if (gd <= gMonthDays[i]) {
      gm = i;
      break;
    }
    gd -= gMonthDays[i];
  }

  const pad = (n) => String(n).padStart(2, '0');
  return `${gy}-${pad(gm)}-${pad(gd)}`;
};

/**
 * تبدیل فیلد تاریخ (ممکن است جلالی یا میلادی باشد)
 */
export const convertDateField = (dateStr) => {
  if (!dateStr) return dateStr;
  const cleaned = String(dateStr).trim();

  const jalaliMatch = cleaned.match(/^(1[34]\d{2})[\/\-](\d{1,2})[\/\-](\d{1,2})$/);
  if (jalaliMatch) {
    const [, jy, jm, jd] = jalaliMatch;
    return jalaliToGregorian(jy, jm, jd);
  }

  return cleaned;
};

/**
 * استخراج نماد، تعداد و قیمت از متن شرح (همان الگوریتم AddTransaction)
 */
export const extractTradeDetailsFromDescription = (description) => {
  if (!description) return { symbol: '', quantity: 0, price: 0 };

  // نرمالایز متن برای تبدیل حروف و اعداد عربی به فارسی
  const desc = normalizePersianText(description.trim());
  const result = { symbol: '', quantity: 0, price: 0 };

  // استخراج تعداد: عدد قبل از "سهم" (با پشتیبانی از کاما)
  const qtyMatch = desc.match(/([\d,]+)\s+سهم/);
  if (qtyMatch) {
    result.quantity = parseInt(String(qtyMatch[1]).replace(/,/g, ''), 10) || 0;
  }

  // استخراج نماد: بین "سهم" و "به"
  const samIndex = desc.indexOf('سهم');
  if (samIndex !== -1) {
    const afterSamIndex = samIndex + 'سهم'.length;
    const beIndex = desc.indexOf('به', afterSamIndex);
    if (beIndex !== -1) {
      const symbolText = desc.substring(afterSamIndex, beIndex).trim();
      const cleanedSymbol = symbolText.replace(/[\s,]+/g, '').trim();
      if (cleanedSymbol && cleanedSymbol.length > 0) {
        result.symbol = cleanedSymbol;
      }
    }
  }

  // استخراج قیمت: عدد بعد از "نرخ" یا "قیمت" (با پشتیبانی از کاما)
  const priceMatch = desc.match(/(?:نرخ|قیمت)\s+([\d,]+)/);
  if (priceMatch) {
    result.price = parseInt(String(priceMatch[1]).replace(/,/g, ''), 10) || 0;
  }

  // Fallback symbol extraction (با پشتیبانی از کاما)
  if (!result.symbol) {
    const pattern = /([\d,]+)\s+سهم\s+([؀-ۿ0-9a-zA-Z]+)/;
    const match = desc.match(pattern);
    if (match && match[2]) {
      result.symbol = match[2].trim();
    }
  }

  // استخراج نماد از پرانتز اگر موجود باشد (مثال: "سایپا(خساپا1)" -> "خساپا")
  // و حذف عدد 1 انتهایی که در فرمت قدیمی به نماد چسبیده است
  if (result.symbol) {
    const parenMatch = result.symbol.match(/\(([^)]+)\)/);
    if (parenMatch) {
      result.symbol = parenMatch[1].replace(/1\s*$/, '').trim();
    }
    // نرمالایز نهایی نماد
    result.symbol = normalizePersianText(result.symbol);
  }

  return result;
};

/**
 * دسته‌بندی شرح بر اساس کلمات کلیدی
 */
export const categorizeAccountingDescription = (description) => {
  if (!description) return 'سایر';
  const desc = String(description).trim();

  // اولویت با عبارات بلندتر و خاص‌تر
  if (desc.includes('شارژ مفید کارت')) return 'شارژ مفید کارت';
  if (desc.includes('مانده نقل از قبل')) return 'مانده نقل از قبل';
  if (desc.includes('سند افتتاحیه')) return 'سند افتتاحیه';
  if (desc.includes('سند اختتامیه')) return 'سند اختتامیه';
  if (desc.includes('حامی')) return 'صندوق حامی';
  if (desc.includes('خرید')) return 'خرید';
  if (desc.includes('فروش')) return 'فروش';
  if (desc.includes('دریافت')) return 'دریافت';
  if (desc.includes('پرداخت')) return 'پرداخت';

  return desc || 'سایر';
};